/**
 * IndexedDB-backed persistent upload queue.
 * Survives reloads, logouts, and browser restarts.
 *
 * Stores:
 *  - files: keyed by localUploadId (uuid). Holds the File blob + metadata.
 *  - parts: keyed by [localUploadId, partNumber]. Holds {ETag, PartNumber, uploadedAt}.
 */

export type UploadStatus =
  | "queued"
  | "uploading"
  | "paused"
  | "failed"
  | "completed"
  | "aborted";

export interface QueuedUpload {
  localUploadId: string;
  userId: string;
  file: File; // Blob persists in IndexedDB
  fileName: string;
  fileSize: number;
  mimeType: string;
  folderId: string | null;
  chunkSize: number;
  totalChunks: number;
  // Server-side multipart context (filled after `init`):
  sessionId?: string;
  r2UploadId?: string;
  r2FileKey?: string;
  // Lifecycle:
  status: UploadStatus;
  error?: string;
  createdAt: number;
  lastAttempt?: number;
  attemptCount: number;
  // Phase C: SHA-256 of file contents for duplicate detection (lowercase hex)
  sha256?: string;
  // Phase C: existing file_entries id if server detected this as a duplicate
  duplicateOfFileId?: string;
  // Phase D: timestamp the upload reached a terminal status (used for pruning)
  completedAt?: number;
}

export interface UploadedPart {
  localUploadId: string;
  partNumber: number;
  etag: string;
  uploadedAt: number;
}

const DB_NAME = "hibane_uploads";
// Phase D: bumped to v3 — adds `by_status` index + completedAt for pruning.
const DB_VERSION = 3;
const FILES_STORE = "files";
const PARTS_STORE = "parts";

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      const oldVersion = event.oldVersion;
      let filesStore: IDBObjectStore;
      if (!db.objectStoreNames.contains(FILES_STORE)) {
        filesStore = db.createObjectStore(FILES_STORE, { keyPath: "localUploadId" });
        filesStore.createIndex("by_user_status", ["userId", "status"]);
        filesStore.createIndex("by_user", "userId");
      } else {
        filesStore = req.transaction!.objectStore(FILES_STORE);
      }
      if (!db.objectStoreNames.contains(PARTS_STORE)) {
        const partsStore = db.createObjectStore(PARTS_STORE, {
          keyPath: ["localUploadId", "partNumber"],
        });
        partsStore.createIndex("by_upload", "localUploadId");
      }
      // v3 migration: add `by_status` index for fast prune queries
      if (oldVersion < 3) {
        if (!filesStore.indexNames.contains("by_status")) {
          filesStore.createIndex("by_status", "status");
        }
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(
  store: string | string[],
  mode: IDBTransactionMode,
  fn: (tx: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  return openDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        let result: T;
        Promise.resolve(fn(t))
          .then((r) => { result = r; })
          .catch(reject);
        t.oncomplete = () => resolve(result);
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error);
      }),
  );
}

function reqToPromise<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

export const uploadQueue = {
  async putUpload(u: QueuedUpload): Promise<void> {
    await tx(FILES_STORE, "readwrite", (t) => {
      t.objectStore(FILES_STORE).put(u);
    });
  },

  async getUpload(localUploadId: string): Promise<QueuedUpload | null> {
    return tx(FILES_STORE, "readonly", async (t) => {
      const r = await reqToPromise(t.objectStore(FILES_STORE).get(localUploadId));
      return (r as QueuedUpload) || null;
    });
  },

  async updateUpload(
    localUploadId: string,
    patch: Partial<QueuedUpload>,
  ): Promise<QueuedUpload | null> {
    return tx(FILES_STORE, "readwrite", async (t) => {
      const store = t.objectStore(FILES_STORE);
      const existing = (await reqToPromise(store.get(localUploadId))) as QueuedUpload | undefined;
      if (!existing) return null;
      const merged: QueuedUpload = { ...existing, ...patch };
      store.put(merged);
      return merged;
    });
  },

  async listForUser(userId: string): Promise<QueuedUpload[]> {
    return tx(FILES_STORE, "readonly", async (t) => {
      const idx = t.objectStore(FILES_STORE).index("by_user");
      const r = await reqToPromise(idx.getAll(IDBKeyRange.only(userId)));
      return (r as QueuedUpload[]) || [];
    });
  },

  async listAll(): Promise<QueuedUpload[]> {
    return tx(FILES_STORE, "readonly", async (t) => {
      const r = await reqToPromise(t.objectStore(FILES_STORE).getAll());
      return (r as QueuedUpload[]) || [];
    });
  },

  async listResumableForUser(userId: string): Promise<QueuedUpload[]> {
    const all = await this.listForUser(userId);
    // Note: `uploading` is intentionally excluded — those are orphans from a prior
    // session and are reconciled to `failed` by the engine on sign-in. The resume
    // banner should only surface genuinely resumable work.
    return all.filter(
      (u) => u.status === "queued" || u.status === "paused",
    );
  },

  async deleteUpload(localUploadId: string): Promise<void> {
    await tx([FILES_STORE, PARTS_STORE], "readwrite", async (t) => {
      t.objectStore(FILES_STORE).delete(localUploadId);
      const idx = t.objectStore(PARTS_STORE).index("by_upload");
      const cursorReq = idx.openKeyCursor(IDBKeyRange.only(localUploadId));
      await new Promise<void>((resolve, reject) => {
        cursorReq.onsuccess = () => {
          const cur = cursorReq.result;
          if (cur) {
            t.objectStore(PARTS_STORE).delete(cur.primaryKey);
            cur.continue();
          } else resolve();
        };
        cursorReq.onerror = () => reject(cursorReq.error);
      });
    });
  },

  async putPart(part: UploadedPart): Promise<void> {
    await tx(PARTS_STORE, "readwrite", (t) => {
      t.objectStore(PARTS_STORE).put(part);
    });
  },

  async getParts(localUploadId: string): Promise<UploadedPart[]> {
    return tx(PARTS_STORE, "readonly", async (t) => {
      const idx = t.objectStore(PARTS_STORE).index("by_upload");
      const r = await reqToPromise(idx.getAll(IDBKeyRange.only(localUploadId)));
      return (r as UploadedPart[]) || [];
    });
  },

  async getPartNumbers(localUploadId: string): Promise<Set<number>> {
    const parts = await this.getParts(localUploadId);
    return new Set(parts.map((p) => p.partNumber));
  },

  /**
   * Phase D: prune old terminal uploads (completed/aborted/failed) older than `maxAgeMs`.
   * Default: 7 days. Failed records are also pruned so users don't see ghost
   * "in progress" entries lingering after a refresh. Returns pruned count.
   */
  async pruneTerminal(maxAgeMs: number = 7 * 24 * 60 * 60 * 1000): Promise<number> {
    const cutoff = Date.now() - maxAgeMs;
    const all = await this.listAll();
    const stale = all.filter((u) => {
      if (u.status !== "completed" && u.status !== "aborted" && u.status !== "failed") return false;
      const ts = u.completedAt ?? u.lastAttempt ?? u.createdAt;
      return ts < cutoff;
    });
    for (const u of stale) await this.deleteUpload(u.localUploadId);
    return stale.length;
  },

  /**
   * Promotes orphaned `uploading` rows (no engine in flight, older than `maxAgeMs`)
   * to `failed`. These are leftovers from a prior session that died mid-flight and
   * would otherwise mislead the UI into showing them as live work.
   */
  async pruneOrphans(maxAgeMs: number = 60 * 60 * 1000): Promise<number> {
    const cutoff = Date.now() - maxAgeMs;
    const all = await this.listAll();
    const orphans = all.filter((u) => {
      if (u.status !== "uploading") return false;
      const ts = u.lastAttempt ?? u.createdAt;
      return ts < cutoff;
    });
    for (const u of orphans) {
      await this.updateUpload(u.localUploadId, {
        status: "failed",
        error: "Interrupted",
        lastAttempt: Date.now(),
      });
    }
    return orphans.length;
  },
};
